import { FullScreenModal, IconButton, Icon, FormikTextField, FormControl, FormikAutocomplete, Button, toasterService } from '@hevoio/hevo-ui-toolkit';
import styles from './CreateVPCModal.module.scss';
import { Field, Formik } from 'formik';
import { Grid } from '@mui/material';
import { AWS_REGIONS, VPC_PEERING_INITIATE_DOC_LINK } from './constants';
import { initialValues, validationSchema } from './configHelper';
import ErrorFocus from '../../../../../form/components/error-focus/ErrorFocus';
import { useRef, useState } from 'react';
import useUserService from '../../../../../auth/hooks/useUserService';
import useService from '../../../../../injector/hooks/useService';
import { MultiRegionService } from '../../../../../auth/services/multi-region.service';

import TeamAPI from '../../../api/TeamAPI';
import { getErrorMessageFromObj } from '../../../../../core/utils/request';
import { SetupGuideDocs } from '../../../../../docs/components/setup-guide-docs/SetupGuideDocs';

export const CreateVPCModal = ({
  open,
  onClose,
  onSuccess
}: {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}) => {
  const formikRef = useRef(null)
  const [submitting, setSubmitting] = useState(false)
  const { user } = useUserService()
  const multiRegionService = useService(MultiRegionService);

  // clusterId is the canonical region identifier (see user.service.ts); the
  // multi-region selection wins for multi-region users when one is set.
  const region =
    (multiRegionService.isMultiRegionUser()
      ? multiRegionService.getSelectedRegionValue()?.id
      : undefined) ?? user.clusterId;
  const onSubmit = () => {
    const { values } = formikRef.current
    const { cidr_range, ...rest } = values
    const cidr = cidr_range.split(',').map((c: string) => c.trim()).filter(Boolean)
    setSubmitting(true)
    return TeamAPI.createPeeringRequest({ ...rest, cidr, team_name: user.teamName, hevo_region: region, team_id: user.teamId }).then(res => {
      toasterService.pop('success', res.message)
      onSuccess?.()
      onClose()
    }).catch(error => {
      toasterService.pop('error', getErrorMessageFromObj(error))
    }).finally(() => {
      setSubmitting(false)
    })
  }
  return (
    <FullScreenModal open={open} onClose={onClose}>
      <div className={'d-flex flex-col h-100 w-100'}>
        <div className={'d-flex justify-between align-items-center p-4 border-bottom w-100'}>
          <p className='m-0 text-label-small text-strong-950'>Create VPC Peering Connection</p>
          <IconButton
            dataId='close-modal'
            label=''
            className={styles.closeModalBtn}
            onClick={() => onClose()}
          >
            <Icon name='close' />
          </IconButton>
        </div>

        <div className='d-flex flex-1 mw-0 overflow-hidden'>
          <div className='flex-1 mw-0 p-6 flex-col gap-4 overflow-auto'>
            <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit} innerRef={formikRef}>
              {(props) => (
                <Grid>
                  <ErrorFocus formik={props}  ></ErrorFocus>
                <Grid container item xs={12} className='mt-6'>
                  <FormControl>
                  <Field
                    name={'name'}
                    dataId='name'
                    label='Connection Name'
                    placeholder='Connection Name'
                    type='string'
                    disableDecimal
                    required
                    inlineError
                    component={FormikTextField}
                    helperText='A unique name for this VPC Peering connection'
                  />
                  </FormControl>
                  </Grid>
                 <Grid container item xs={12} className='mt-6'>
                  <FormControl>
                  <Field
                    name={'vpc_id'}
                    dataId='vpc_id'
                    label='VPC ID'
                    placeholder='VPC ID'
                    type='string'
                    disableDecimal
                    required
                    inlineError
                    component={FormikTextField}
                    helperText='The ID of the AWS VPC where your Source or Destination database is located.'
                  />
                  </FormControl>
                  </Grid>

                  <Grid container item xs={12} className='mt-6'>
                  <FormControl>
                  <Field
                    name={'cidr_range'}
                    dataId='cidr_range'
                    label='CIDR Range'
                    placeholder='CIDR Range'
                    type='string'
                    disableDecimal
                    required
                    inlineError
                    component={FormikTextField}
                    helperText='The IP address range of your AWS VPC in CIDR format. For example, 172.31.0.0/16'
                  />
                  </FormControl>
                  </Grid>

                  <Grid container item xs={12} className='mt-6'>
                  <FormControl>
                  <Field
                    name={'aws_account_id'}
                    dataId='aws_account_id'
                    label='AWS Account ID'
                    placeholder='AWS Account ID'
                    type='string'
                    disableDecimal
                    required
                    inlineError
                    component={FormikTextField}
                    helperText='The 12-digit ID of the AWS account that contains the VPC you want to peer.'
                  />
                  </FormControl>
                </Grid>

                <Grid container item xs={12} className='mt-6'>
                  <FormControl>
                  <Field
                    name={'aws_region'}
                    dataId='aws_region'
                    label='AWS Region'
                    placeholder='AWS Region'
                    type='string'
                    disableDecimal
                    required
                    inlineError
                    component={FormikAutocomplete}
                    textFieldProps={{
                      label: 'AWS Region',
                      required: true,
                      helperText: 'The AWS region where your Source or Destination database is hosted.'
                    }}
                    options={AWS_REGIONS}
                  />
                  </FormControl>
                </Grid>
               </Grid>

              )}
            </Formik>
          </div>

          <SetupGuideDocs
            className={styles.setupGuideDocs}
            docLink={VPC_PEERING_INITIATE_DOC_LINK}
          />
        </div>

        <div className='border-top p-2 w-100 d-flex justify-center'>
          <Button  variation='outline' onClick={onClose}>
            Cancel
          </Button>
          <Button className='ml-2' disabled={submitting} onClick={()=>formikRef.current.submitForm()}>Create</Button>
        </div>
      </div>
    </FullScreenModal>
  );
};
